import { create } from 'zustand';
import { AuthState, User } from '../types';
import { authService } from '../services/auth';

interface AuthStore extends AuthState {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,

  signIn: async () => {
    try {
      await authService.signIn();
    } catch (error) {
      console.error('Auth store sign in failed:', error);
      throw error;
    }
  },

  signOut: async () => {
    try {
      await authService.signOut();
      set({ user: null, token: null, isAuthenticated: false });
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  },

  checkAuth: async () => {
    set({ isLoading: true });
    
    try {
      const [token, oidcUser] = await Promise.all([
        authService.getToken(),
        authService.getUser(),
      ]);

      if (token && oidcUser) {
        // Get comprehensive user data from API using the token
        try {
          const { apiService } = await import('../services/api');
          const fullUser = await apiService.getCurrentUser();
          
          // Add profile picture from OIDC if available
          const user = {
            ...fullUser,
            picture: oidcUser.picture || fullUser.picture
          };
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (apiError) {
          console.warn('Failed to get full user profile from API, using OIDC data:', apiError);
          
          // If we got a 401, the token might need renewal
          if (apiError?.message?.includes('Authentication')) {
            console.log('Token might be invalid, will retry on next navigation');
          }
          
          // Fallback to OIDC user data if API fails
          set({
            user: oidcUser,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } else {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  setUser: (user) => {
    set({ user, isAuthenticated: !!user });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },
}));